import { Fragment, type ReactNode } from 'react';
import type { Environment } from '@customerio/hydra-ui-api';
import {
  Box,
  Button,
  Code,
  Grid,
  Heading,
  Icon,
  InlineStack,
  Link,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Stack,
  Table,
  TableTbody,
  TableTh,
  TableThead,
  TableTr,
  Tooltip,
  VisuallyHidden,
} from '@customerio/pluma-components/react';
import { useFieldArray } from 'react-hook-form';
import { FormProvider, useWatch, type UseZodFormReturn } from '@/components/form';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import {
  hiddenAttrsAfterAdd,
  hiddenAttrsAfterRemove,
  hiddenAttrsAfterRename,
  type AttributesFormInput,
} from '../utils/attributes-form';
import {
  enabledIdentifiers,
  isAttributeEnabledIdentifier,
  isEmailIdentified,
  type IdentifiersConfig,
} from '../utils/identifiers';
import { IdentifierKey } from '../utils/attribute-keys';
import { AttributeEditorRow } from './attribute-editor-row';
import { AvailableAttributes } from './available-attributes';

const IDENTIFYING_PEOPLE_DOCS_URL = 'https://docs.customer.io/identifying-people';
const KEY_HEADER_STYLE = { border: 'none', width: 300 };
const VALUE_HEADER_STYLE = { border: 'none' };

/** The environment fields the editor reads. */
export type PersonAttributesEnvironment = Pick<
  Environment,
  'identifier_type' | 'identifiers' | 'sent_attributes' | 'more_attributes'
>;

type PersonAttributesEditorProps = {
  /** The attribute form, owned by the page so its header can save and discard. */
  form: UseZodFormReturn<AttributesFormInput>;
  environment: PersonAttributesEnvironment;
  /** Whether the viewer holds `journeys/people/edit`. */
  canEdit: boolean;
  isSaving: boolean;
};

type VisibleRow = {
  id: string;
  index: number;
  /** The row's current name; what the catalog treats as taken. */
  key: string;
  /** The name the row loaded with, which decides where the row sits: `''` for a created row. */
  originalKey: string;
};

type AttributesTableProps = {
  caption: string;
  note?: string;
  rows: VisibleRow[];
  identifiers: IdentifiersConfig | null | undefined;
  isEmailIdentified: boolean;
  isSaving: boolean;
  onKeyChange: (index: number) => void;
  onChange: () => void;
  onRemove: (index: number) => void;
};

function AttributesTable({ caption, note, rows, ...rowProps }: AttributesTableProps) {
  return (
    <>
      <Box pt="200" pb="050" px="300">
        <InlineStack gap="100" alignBlock="center">
          <Heading level="4">{caption}</Heading>
          {note != null ? (
            <Paragraph size="xs" variant="secondary">
              {note}
            </Paragraph>
          ) : null}
        </InlineStack>
      </Box>
      <Table caption={caption} layout="fixed" isBorderless>
        <TableThead>
          <TableTr>
            <TableTh p="0" style={KEY_HEADER_STYLE}>
              <VisuallyHidden>Attribute name</VisuallyHidden>
            </TableTh>
            <TableTh p="0" style={VALUE_HEADER_STYLE}>
              <VisuallyHidden>Attribute value</VisuallyHidden>
            </TableTh>
          </TableTr>
        </TableThead>
        <TableTbody>
          {rows.map((row) => (
            <AttributeEditorRow key={row.id} index={row.index} fieldId={row.id} {...rowProps} />
          ))}
        </TableTbody>
      </Table>
    </>
  );
}

/** "a and b", or "a, b, and c" — the identifier names as inline code. */
function joinCodeSnippets(names: readonly string[]): ReactNode {
  return names.map((name, index) => {
    const isLast = index === names.length - 1;
    const separator = isLast ? '' : names.length > 2 ? (index === names.length - 2 ? ', and ' : ', ') : ' and ';
    return (
      <Fragment key={name}>
        <Code>{name}</Code>
        {separator}
      </Fragment>
    );
  });
}

type IdentifierGuidePanelProps = {
  identifierNames: readonly string[];
  isEmailIdentified: boolean;
};

function IdentifierGuidePanel({ identifierNames, isEmailIdentified }: IdentifierGuidePanelProps) {
  const taxonomy = useProductTaxonomy();
  const singular = taxonomy.term('people.singular');
  const plural = taxonomy.term('people.plural');
  const snippets = joinCodeSnippets(isEmailIdentified ? [IdentifierKey.Email] : identifierNames);
  const alternatives = `${identifierNames.length > 1 ? 'either ' : ''}${identifierNames.join(' or ')}`;

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle display="flex" alignItems="center" gap="100">
          <Icon name="tip" size="lg" color="subtle" />
          <span>About identifier attributes</span>
        </PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Stack gap="200">
          <Paragraph>
            An identifier is a unique value used to identify {plural} when adding, updating, or tracking a profile.
          </Paragraph>
          <Paragraph>
            {snippets} must be unique for each {singular} and cannot be changed.{' '}
            <Tooltip content={`Include ${alternatives} to add a ${singular}.`} isCustom>
              {(context) => (
                <Link
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                  href={IDENTIFYING_PEOPLE_DOCS_URL}
                  variant="secondary"
                  isExternal
                >
                  Learn more about identifying {plural}.
                </Link>
              )}
            </Tooltip>
          </Paragraph>
        </Stack>
      </PanelSection>
    </Panel>
  );
}

/**
 * The attribute editor for a person being added: identifier rows, other
 * attribute rows, a button for a new attribute, and the workspace's attribute
 * catalog to add from. The page owns the form and its save/discard actions.
 */
export function PersonAttributesEditor({ form, environment, canEdit, isSaving }: PersonAttributesEditorProps) {
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'attributes' });
  const rows = useWatch({ control: form.control, name: 'attributes' });
  const identifiers = environment.identifiers;
  const emailIdentified = isEmailIdentified(environment);
  const identifierNames = enabledIdentifiers(identifiers);

  // A row keeps the table it loaded into, so renaming a custom attribute never moves it. `cio_id` is
  // assigned by the backend, and an email-identified workspace assigns `id` too, so neither is
  // offered for editing; those rows stay in the form so the save sends them as blanks.
  const visibleRows: VisibleRow[] = fields.flatMap((field, index) => {
    const row = rows[index];
    if (
      row == null ||
      row.originalKey === IdentifierKey.CioId ||
      (emailIdentified && row.originalKey === IdentifierKey.Id)
    ) {
      return [];
    }
    return [{ id: field.id, index, key: row.key, originalKey: row.originalKey }];
  });
  const identifierRows = visibleRows.filter((row) => isAttributeEnabledIdentifier(row.originalKey, identifiers));
  const otherRows = visibleRows.filter((row) => !isAttributeEnabledIdentifier(row.originalKey, identifiers));

  const setHiddenAttrs = (hiddenAttrs: string[]) => {
    form.setValue('hiddenAttrs', hiddenAttrs, { shouldDirty: true });
  };

  // Every row's errors show as soon as the form is edited — a lone identifier's
  // blank value is reported even while the user is typing elsewhere — so each
  // edit revalidates the whole form rather than only the field that changed.
  const revalidate = () => {
    void form.trigger();
  };

  const handleKeyChange = (index: number) => {
    setHiddenAttrs(hiddenAttrsAfterRename(form.getValues(), index));
  };

  const handleRemove = (index: number) => {
    const row = rows[index];
    if (row != null) {
      setHiddenAttrs(hiddenAttrsAfterRemove(form.getValues('hiddenAttrs'), row));
    }
    remove(index);
    revalidate();
  };

  const handleAdd = (attribute: string) => {
    setHiddenAttrs(hiddenAttrsAfterAdd(form.getValues('hiddenAttrs'), attribute));
    append({ key: attribute, value: '', originalKey: attribute }, { shouldFocus: false });
    revalidate();
  };

  const handleCreate = () => {
    append({ key: '', value: '', originalKey: '' });
    revalidate();
  };

  const rowProps = {
    identifiers,
    isEmailIdentified: emailIdentified,
    isSaving,
    onKeyChange: handleKeyChange,
    onChange: revalidate,
    onRemove: handleRemove,
  };

  return (
    <FormProvider {...form}>
      <Grid columns="2fr 1fr" gap="200">
        <Panel as="section" withSectionBorders data-test="attributes-edit-section">
          <PanelHeader>
            <PanelTitle>Attributes</PanelTitle>
          </PanelHeader>

          <PanelSection withPadding={false}>
            <AttributesTable
              caption="Identifiers"
              note={identifierNames.length > 1 && !emailIdentified ? 'Include at least one' : undefined}
              rows={identifierRows}
              {...rowProps}
            />
          </PanelSection>

          <PanelSection withPadding={false}>
            <AttributesTable caption="Other attributes" rows={otherRows} {...rowProps} />
            <Box mt="200" ml="250" mb="250">
              <Button
                icon="add"
                isDisabled={isSaving || !canEdit}
                onClick={handleCreate}
                data-test="create-new-attribute"
              >
                Create new attribute
              </Button>
            </Box>
          </PanelSection>
        </Panel>

        <Stack as="aside" gap="200">
          <IdentifierGuidePanel identifierNames={identifierNames} isEmailIdentified={emailIdentified} />
          <Panel>
            <PanelHeader>
              <PanelTitle>Available Attributes</PanelTitle>
            </PanelHeader>
            <PanelSection>
              <AvailableAttributes
                sentAttributes={environment.sent_attributes}
                moreAttributes={environment.more_attributes}
                identifiers={identifiers}
                currentAttributeKeys={visibleRows.map((row) => row.key)}
                isSaving={isSaving}
                onAddAttribute={handleAdd}
              />
            </PanelSection>
          </Panel>
        </Stack>
      </Grid>
    </FormProvider>
  );
}
